/*
  This file is used to create a generic context for fetching data from the API.
  It is used to fetch data from the API and store it in the state.
*/

import React, { createContext, useState, useContext, useEffect, useCallback, useRef } from 'react';
import { fetchResource } from '../api/fetchResource';
import logger from '../services/logger';
import { queryErrorAlertMessage } from '../utils/queryErrorMessage';

interface DataContextType<T, Q> {
  data: T[];
  submittedQuery: Q | null;
  loading: boolean;
  error: string | null;
  fetchData: (queryParams: Q) => Promise<void>;
  clearData: () => void;
}

// Generic Context Creator
export const createDataContext = <T, Q>(
  apiName: string,
  path: string,
  parseResponse: (response: any) => T[],
  options: { validateQueryParams?: (value: unknown) => value is Q } = {},
) => {

  const DataContext = createContext<DataContextType<T, Q>>({
    data: [],
    submittedQuery: null,
    loading: false,
    error: null,
    fetchData: async () => {
      console.log('Default fetchData called - context not properly initialized');
    },
    clearData: () => { }
  });

  const useDataContext = () => useContext(DataContext);

  const DataProvider = ({ children, initialData }: { children: React.ReactNode, initialData?: T[] }) => {
    // Opt-in query validation stores rows and their originating query together.
    // Two independent storage keys could pair one tab's rows with another's dates.
    const [result, setResult] = useState<{ data: T[]; submittedQuery: Q | null }>(() => {
      try {
        const storedData = localStorage.getItem(`${apiName}-${path}-data`);
        if (storedData) {
          const parsed = JSON.parse(storedData);
          if (options.validateQueryParams) {
            if (parsed && !Array.isArray(parsed) && options.validateQueryParams(parsed.submittedQuery)) {
              return { data: parseResponse(parsed.data), submittedQuery: parsed.submittedQuery };
            }
            // Legacy rows have no trustworthy query to restore alongside them.
          } else if (Array.isArray(parsed)) {
            return { data: parseResponse(parsed), submittedQuery: null };
          }
        }
      } catch { /* Recover from unreadable or obsolete saved results. */ }
      return { data: initialData || [], submittedQuery: null };
    });
    const { data, submittedQuery } = result;

    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const requestId = useRef(0);
    const pending = useRef<{ key: string; controller: AbortController; promise: Promise<void> } | null>(null);

    useEffect(() => {
      try {
        localStorage.setItem(`${apiName}-${path}-data`, JSON.stringify(options.validateQueryParams ? result : data));
      } catch { /* The page still works when storage is unavailable. */ }
    }, [data, result]);

    useEffect(() => () => {
      requestId.current += 1;
      pending.current?.controller.abort();
      pending.current = null;
    }, []);

    const fetchData = useCallback((queryParams: Q): Promise<void> => {
      const key = JSON.stringify(queryParams);
      if (pending.current?.key === key) return pending.current.promise;

      pending.current?.controller.abort();
      const controller = new AbortController();
      const id = ++requestId.current;
      setResult({ data: [], submittedQuery: queryParams });
      setLoading(true);
      setError(null);
      const promise = (async () => {
        try {
          const parsed = await fetchResource<T, Q>(apiName, path, parseResponse, queryParams, controller.signal);
          if (id === requestId.current) setResult({ data: parsed, submittedQuery: queryParams });
        } catch (err) {
          if (id !== requestId.current) return;
          setError(queryErrorAlertMessage(err));
          void Promise.resolve().then(() => logger.log(`Error fetching data: ${err}`, { source: { file: __filename } })).catch(() => {});
        } finally {
          if (id === requestId.current) {
            pending.current = null;
            setLoading(false);
          }
        }
      })();
      pending.current = { key, controller, promise };
      return promise;
    }, []);

    const clearData = useCallback(() => {
      requestId.current += 1;
      pending.current?.controller.abort();
      pending.current = null;
      setResult({ data: [], submittedQuery: null });
      setError(null);
      setLoading(false);
    }, []);

    return (
      <DataContext.Provider value={{ data, submittedQuery, loading, error, fetchData, clearData }}>
        {children}
      </DataContext.Provider>
    );
  };

  return { useDataContext, DataProvider };
};
